import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, InputNumber, Popconfirm, message, Space, Tag } from "antd";
import { Plus, Eye } from "lucide-react";
import dayjs from "dayjs";
import { useLoneWorkerSessions, useLoneWorkerCheckins, useLoneWorkerSessionCreate, useLoneWorkerSessionUpdate, useLoneWorkerSessionDelete, useCCUsers } from "../../../../api-hooks/compliance";

const STATUS_COLOR: Record<string, string> = { active: "blue", completed: "green", sos: "red", missed_checkin: "orange", resolved: "default" };

export default function LoneWorkerTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [checkinsModal, setCheckinsModal] = useState<{ open: boolean; sessionId: number | null }>({ open: false, sessionId: null });
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);

  const { data: res, isLoading } = useLoneWorkerSessions(cid, { page, limit: 20 });
  const { data: checkins = [] } = useLoneWorkerCheckins(cid, checkinsModal.sessionId ?? 0);
  const { data: users = [] } = useCCUsers(cid);
  const sessions = (res as any)?.data ?? [];
  const total = (res as any)?.total ?? 0;

  const cb = (msg: string) => ({ onSuccess: () => { msgApi.success(msg); setModal({ open: false, rec: null }); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useLoneWorkerSessionCreate(cid, cb("Session created."));
  const update = useLoneWorkerSessionUpdate(cid, cb("Updated."));
  const remove = useLoneWorkerSessionDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const columns = [
    { title: "Worker", dataIndex: "user_name", key: "u" },
    { title: "Start", dataIndex: "start_time", key: "s", render: (v: string) => v ? dayjs(v).format("DD/MM/YY HH:mm") : "—" },
    { title: "End", dataIndex: "end_time", key: "e", render: (v: string) => v ? dayjs(v).format("DD/MM/YY HH:mm") : "—" },
    { title: "Interval (min)", dataIndex: "check_interval_mins", key: "i" },
    { title: "Status", dataIndex: "status", key: "st", render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v?.replace(/_/g, " ").toUpperCase()}</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" icon={<Eye size={12} />} onClick={() => setCheckinsModal({ open: true, sessionId: r.id })}>Checkins</Button>
        <Button size="small" onClick={() => { setModal({ open: true, rec: r }); form.setFieldsValue(r); }}>Update</Button>
        <Popconfirm title="Delete?" onConfirm={() => remove.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  const checkinCols = [
    { title: "Checked In At", dataIndex: "checked_in_at", key: "t", render: (v: string) => v ? dayjs(v).format("DD/MM/YY HH:mm:ss") : "—" },
    { title: "Status", dataIndex: "status", key: "s", render: (v: string) => <Tag color={{ ok: "green", concern: "orange", sos: "red" }[v]}>{v?.toUpperCase()}</Tag> },
    { title: "Lat", dataIndex: "lat", key: "lat", render: (v: any) => v || "—" },
    { title: "Lng", dataIndex: "lng", key: "lng", render: (v: any) => v || "—" },
    { title: "Notes", dataIndex: "notes", key: "n", render: (v: string) => v || "—" },
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-3">
        <h3 className="font-semibold text-slate-700 text-sm">Lone Worker Sessions</h3>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setModal({ open: true, rec: null }); form.resetFields(); form.setFieldsValue({ check_interval_mins: 30 }); }} style={{ background: "#4c1d95" }}>New Session</Button>
      </div>
      <Table rowKey="id" dataSource={sessions} columns={columns} loading={isLoading} size="small" pagination={{ current: page, pageSize: 20, total, onChange: setPage }} />

      {/* Create/Update Session */}
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title={modal.rec ? "Update Session" : "New Lone Worker Session"} width={420}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          if (modal.rec) update.mutate({ id: modal.rec.id, status: vals.status, notes: vals.notes });
          else create.mutate(vals);
        }}>
          {!modal.rec && (
            <Form.Item name="user_id" label="Worker" rules={[{ required: true }]}><Select showSearch options={(users as any[]).map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}`.trim() || u.email }))} /></Form.Item>
          )}
          {modal.rec && (
            <Form.Item name="status" label="Status"><Select options={["active","completed","sos","missed_checkin","resolved"].map(v => ({ value: v, label: v.replace(/_/g," ").toUpperCase() }))} /></Form.Item>
          )}
          {!modal.rec && (
            <Form.Item name="check_interval_mins" label="Check-in Interval (mins)"><InputNumber className="w-full" min={5} max={120} /></Form.Item>
          )}
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending || update.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>

      {/* Checkins Viewer */}
      <Modal open={checkinsModal.open} onCancel={() => setCheckinsModal({ open: false, sessionId: null })} footer={null} title={`Checkins — Session #${checkinsModal.sessionId}`} width={600}>
        <Table rowKey="id" dataSource={checkins as any[]} columns={checkinCols} size="small" pagination={false} />
      </Modal>
    </div>
  );
}
